// Structured logging service with Datadog batch transport
// Format: [LEVEL] uid:xxx | area | message
// Batches logs and POSTs to /api/log for server-side forwarding to Datadog

type LogLevel = 'info' | 'warn' | 'error';
export type LogMeta = Record<string, unknown> | unknown[];

export interface LogContext {
  userId?: string;
  area: string; // e.g., 'checkout', 'plans', 'nav', 'api'
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  service: string;
  area: string;
  env: string;
  message: string;
  ddsource: string;
  ddtags: string;
  userId?: string;
  sessionId?: string;
  error?: { message: string; stack?: string; name: string };
  meta?: unknown;
}

// --- Environment detection ---
function getEnv(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host === 'orders.2turniton.com') return 'production';
  if (host.includes('netlify.app')) return 'deploy-preview';
  return 'dev';
}

// --- Queue & transport ---
const queue: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_URL = '/api/log';

function getSessionId(): string | null {
  try {
    return (window as unknown as { posthog?: { get_session_id: () => string } })
      .posthog?.get_session_id() ?? null;
  } catch {
    return null;
  }
}

// Get userId from sessionStorage if not provided in context
function resolveUserId(ctx: LogContext): string {
  if (ctx.userId) {
    return ctx.userId;
  }

  try {
    const stored = sessionStorage.getItem('order-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      const userId = parsed.state?.userId;
      if (userId) {
        return userId;
      }
    }
  } catch {
    // Ignore localStorage errors
  }

  return 'anon';
}

function enqueue(level: LogLevel, ctx: LogContext, message: string, err?: Error, meta?: LogMeta) {
  const uid = resolveUserId(ctx);
  const env = getEnv();

  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    service: '2tio-orders-web',
    area: ctx.area,
    env,
    message: `uid:${uid} | ${ctx.area} | ${message}`,
    ddsource: 'browser',
    ddtags: `env:${env},service:2tio-orders-web,area:${ctx.area}`,
  };

  if (uid !== 'anon') entry.userId = uid;
  const sid = getSessionId();
  if (sid) entry.sessionId = sid;
  if (err) {
    entry.error = { message: err.message, stack: err.stack, name: err.name };
  }
  if (meta) {
    entry.meta = meta;
  }

  queue.push(entry);

  // Flush immediately on error
  if (level === 'error') {
    flush();
  } else {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function flush() {
  if (queue.length === 0) return;
  const entries = queue.splice(0);

  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  // Use sendBeacon for reliability during page unload, fetch otherwise.
  // JSON.stringify is inside the try: a circular/over-nested meta object can make it
  // throw (TypeError / RangeError "Maximum call stack"). Logging must never break the app.
  try {
    const payload = JSON.stringify(entries);
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(FLUSH_URL, new Blob([payload], { type: 'application/json' }));
      if (!sent) {
        // Fallback to fetch if sendBeacon fails
        fetchFlush(payload);
      }
    } else {
      fetchFlush(payload);
    }
  } catch {
    // Silently fail — logging should never break the app
  }
}

function fetchFlush(payload: string) {
  fetch(FLUSH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Silently fail
  });
}

// --- Lifecycle hooks ---
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('beforeunload', () => flush());
}

// --- Console output (preserved for Netlify log viewer / dev tools) ---
function stringifyMeta(meta?: LogMeta): string {
  if (!meta) return '';
  try {
    return ` | meta:${JSON.stringify(meta)}`;
  } catch {
    return ' | meta:[unserializable]';
  }
}

function log(level: LogLevel, ctx: LogContext, message: string, meta?: LogMeta) {
  const uid = resolveUserId(ctx);
  const logMessage = `[${level.toUpperCase()}] uid:${uid} | ${ctx.area} | ${message}${stringifyMeta(meta)}`;

  switch (level) {
    case 'error':
      console.error(logMessage);
      break;
    case 'warn':
      console.warn(logMessage);
      break;
    default:
      console.log(logMessage);
  }
}

// --- Public API (unchanged signature) ---
export const logger = {
  info: (ctx: LogContext, msg: string, meta?: LogMeta) => {
    log('info', ctx, msg, meta);
    enqueue('info', ctx, msg, undefined, meta);
  },
  warn: (ctx: LogContext, msg: string, meta?: LogMeta) => {
    log('warn', ctx, msg, meta);
    enqueue('warn', ctx, msg, undefined, meta);
  },
  error: (ctx: LogContext, msg: string, err?: Error, meta?: LogMeta) => {
    const errorMsg = err ? `${msg}: ${err.message}` : msg;
    log('error', ctx, errorMsg, meta);
    enqueue('error', ctx, msg, err, meta);
  },
  /** Force flush all queued entries (e.g., before navigation) */
  flush,
};
